import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation, matchPath } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import { menuSelectors } from 'reducers';
import { menuToggled, menuPreviewStarted, menuPreviewEnded } from 'reducers/menu';

import { Box, Collapse, Link, Button } from '@mui/material';
import { ChevronLeft, ChevronRight } from 'mdi-material-ui';

const MenuItem = ({ menuItem, activeRoute }) => {
    const isActive = menuItem.path === activeRoute?.path;
    return (
        <Link
            key={menuItem.label}
            color={isActive ? 'primary' : 'inherit'}
            sx={theme => ({
                padding: '10px 0',
                fontSize: 13,
                textTransform: 'uppercase',
                textDecoration: 'none',
                display: 'block',
                color: isActive ? theme.palette.primary.main : theme.palette.common.white,
            })}
            to={menuItem.absolutePath || menuItem.path}
            href={menuItem.absolutePath || menuItem.path}
            component={RouterLink}
        >
            {menuItem.label}
        </Link>
    );
};

MenuItem.propTypes = {
    menuItem: PropTypes.object,
    activeRoute: PropTypes.object,
};

const MenuGroup = ({ label, routes, activeRoute, expanded, onToggle }) => {
    return (
        <React.Fragment>
            <Box
                onClick={() => onToggle(label)}
                sx={{
                    padding: '10px 0',
                    fontSize: 13,
                    textTransform: 'uppercase',
                    textDecoration: 'none',
                    color: 'common.white',
                    display: 'flex',
                    flexWrap: 'nowrap',
                    cursor: 'pointer',
                    alignItems: 'center',
                    marginLeft: '-20px',
                }}
            >
                <ChevronRight
                    fontSize="small"
                    sx={{
                        mt: '-2px',
                        transition: 'transform .2s',
                        transform: expanded ? 'rotate(90deg)' : 'none',
                    }}
                />
                {label}
            </Box>
            <Collapse
                in={expanded}
                timeout="auto"
                sx={{
                    '& .MuiCollapse-wrapperInner': {
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        paddingLeft: '20px',
                    },
                }}
                id={`menu-${label.replace(/\w/gi, '')}`}
            >
                {routes.map(route => (
                    <MenuItem key={route.label} menuItem={route} activeRoute={activeRoute} />
                ))}
            </Collapse>
        </React.Fragment>
    );
};

MenuGroup.propTypes = {
    label: PropTypes.string,
    routes: PropTypes.array,
    activeRoute: PropTypes.object,
    expanded: PropTypes.bool,
    onToggle: PropTypes.func,
};

const Menu = props => {
    const { routes } = props;
    const location = useLocation();
    const dispatch = useDispatch();
    const hideTimeoutRef = useRef(null);

    const open = useSelector(menuSelectors.getMenuOpenState);
    const isPreview = useSelector(menuSelectors.getMenuPreviewState);
    const isVisible = useSelector(menuSelectors.getMenuVisibleState);
    const activeRoute = useMemo(
        () => routes.find(r => matchPath(r, location.pathname)),
        [routes, location]
    );

    // Manage group expansion states
    const [expandedGroups, setExpandedGroups] = useState({});

    // Initialize expanded groups based on active routes
    useEffect(() => {
        if (activeRoute?.parentLabel) {
            setExpandedGroups(prev => ({
                ...prev,
                [activeRoute.parentLabel]: true,
            }));
        }
    }, [activeRoute]);

    const menu = useMemo(() => {
        const localMenu = [];
        routes.forEach(route => {
            if (route.parentLabel) {
                const group = localMenu.find(
                    item => item.isGroup && item.label === route.parentLabel
                );
                if (group) {
                    group.routes.push(route);
                } else {
                    localMenu.push({ isGroup: true, label: route.parentLabel, routes: [route] });
                }
            } else {
                localMenu.push(route);
            }
        });
        return localMenu;
    }, [routes]);

    const handleDrawerToggle = useCallback(() => {
        dispatch(menuToggled());
    }, [dispatch]);

    const handleToggleHover = useCallback(() => {
        if (!open) {
            dispatch(menuPreviewStarted());
        }
    }, [dispatch, open]);

    const handleMenuLeave = useCallback(() => {
        if (isPreview) {
            // Add a small delay before hiding to prevent flickering
            hideTimeoutRef.current = setTimeout(() => {
                dispatch(menuPreviewEnded());
            }, 100);
        }
    }, [dispatch, isPreview]);

    const handleMenuEnter = useCallback(() => {
        // Cancel any pending hide timeout when entering menu area
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
    }, []);

    // Handle group toggle
    const handleGroupToggle = useCallback(groupLabel => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupLabel]: !prev[groupLabel],
        }));
    }, []);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
            }
        };
    }, []);

    return (
        <>
            {/* Always visible toggle button - positioned absolutely */}
            <Button
                variant="contained"
                onClick={handleDrawerToggle}
                onMouseEnter={handleToggleHover}
                size="small"
                sx={theme => ({
                    position: 'fixed',
                    top: '110px',
                    left: open ? `${theme.constants.sidebarWidth - 48}px` : 0,
                    zIndex: theme.zIndex.drawer + 1,
                    backgroundColor: isVisible ? 'common.black' : 'common.white',
                    color: isVisible ? 'common.white' : 'common.black',
                    borderRadius: open ? '5px' : '0 5px 5px 0',
                    border: isVisible ? 0 : `1px solid ${theme.palette.primary.main}`,
                    minWidth: 0,
                    transition: theme.transitions.create(['left', 'background-color'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                    boxShadow: 'none',
                    padding: '4px',
                    '&:hover': {
                        color: 'primary.main',
                        backgroundColor: 'common.black',
                    },
                })}
            >
                <ChevronLeft
                    sx={theme => ({
                        padding: 0,
                        transition: theme.transitions.create('transform', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.leavingScreen,
                        }),
                        transform: open ? 'none' : 'rotate(180deg)',
                    })}
                />
            </Button>

            {/* Menu container */}
            <Box
                id="sidebar-menu"
                onMouseEnter={handleMenuEnter}
                onMouseLeave={handleMenuLeave}
                sx={theme => ({
                    backgroundColor: 'common.black',
                    width: isVisible ? `${theme.constants.sidebarWidth}px` : 0,
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'fixed',
                    height: `calc(100% - ${theme.constants.appBarHeight}px)`,
                    overflow: 'hidden',
                    opacity: isPreview ? 0.9 : 1, // Slightly transparent in preview mode
                    transition: theme.transitions.create(['width', 'opacity'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                    zIndex: theme.zIndex.drawer,
                })}
            >
                {/* Menu content - visible when open or in preview */}
                {isVisible && (
                    <>
                        {/* Spacer for the toggle button */}
                        <Box sx={{ height: '56px', flexShrink: 0 }} />

                        {/* Scrollable menu content */}
                        <Box
                            sx={{
                                flex: 1,
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                padding: '0 33px 60px',
                                '&::-webkit-scrollbar': {
                                    width: '4px',
                                },
                                '&::-webkit-scrollbar-track': {
                                    backgroundColor: 'common.black',
                                    borderRadius: 0,
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: 'primary.main',
                                    borderRadius: '3px',
                                    '&:hover': {
                                        backgroundColor: 'primary.dark',
                                        cursor: 'pointer',
                                    },
                                },
                            }}
                        >
                            {menu.map(menuItem => {
                                if (menuItem.isGroup) {
                                    return (
                                        <MenuGroup
                                            key={menuItem.label}
                                            label={menuItem.label}
                                            routes={menuItem.routes}
                                            activeRoute={activeRoute}
                                            expanded={!!expandedGroups[menuItem.label]}
                                            onToggle={handleGroupToggle}
                                        />
                                    );
                                }

                                return (
                                    <MenuItem
                                        key={menuItem.key}
                                        menuItem={menuItem}
                                        activeRoute={activeRoute}
                                    />
                                );
                            })}
                        </Box>
                    </>
                )}
            </Box>
        </>
    );
};

Menu.propTypes = { routes: PropTypes.array };

export default Menu;
